import { useFormatters } from '@/hooks/useFormatters';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import DeviceNameSpan from '@components/deviceNameSpan';
import GlobalModal from '@components/GlobalModal';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import { Access } from '@features/authorization';
import { useGateMovementsHistory } from '@features/gateCommon/hooks/useGateMovementsHistory';
import StandardTable from '@features/standardDesign/components/StandardTable';
import StandardListPagination from '@features/standardDesign/components/listView/StandardListPagination';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Skeleton } from '@mui/material';
import { type FC, Suspense, useState } from 'react';

const DEFAULT_ROWS_PER_PAGE = 20;

interface Props {
  gateId: string;
}

const PaginatedHistoryContent: FC<Props> = ({ gateId }) => {
  const { formatMetricValue, formatDateTime } = useFormatters();
  const { i18n } = useLingui();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { items, pageInfo, unit, precision } = useGateMovementsHistory(gateId, page, rowsPerPage);

  const NO_DATA_MESSAGE = t(i18n)`No hay datos`;

  const formatTimestamp = (timestamp: number | null | undefined) =>
    timestamp != null ? formatDateTime(timestamp) : NO_DATA_MESSAGE;

  const formatPosition = (position: number | null | undefined) =>
    position != null ? formatMetricValue(position, { unit, precision }) : NO_DATA_MESSAGE;

  const headers = [
    <Trans>ID</Trans>,
    <Trans>Solicitado por</Trans>,
    <Trans>Fecha</Trans>,
    <Trans>Posición solicitada</Trans>,
    <Trans>Posición de inicio</Trans>,
    <Trans>Posición final</Trans>,
    <Trans>Último mensaje</Trans>,
    <Trans>Fecha inicio</Trans>,
    <Trans>Fecha finalización</Trans>,
  ];

  const rows = items.map((mov) => ({
    id: mov.id,
    cells: [
      mov.id,
      mov.requestedBy.name || NO_DATA_MESSAGE,
      formatTimestamp(mov.requestedPosition[0]),
      formatPosition(mov.requestedPosition[1]),
      formatPosition(mov.startPosition?.[1]),
      formatPosition(mov.finishPosition?.[1]),
      mov.messages.at(0)?.type ?? NO_DATA_MESSAGE,
      formatTimestamp(mov.startPosition?.[0]),
      formatTimestamp(mov.finishPosition?.[0]),
    ],
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <StandardTable
        headers={headers}
        rows={rows}
        slotProps={{
          tableContainer: {
            component: 'div',
            sx: { flex: 1, minHeight: 0, border: 1, borderColor: 'divider', borderRadius: 1 },
          },
        }}
      />
      <StandardListPagination
        totalItems={pageInfo.totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(rpp) => {
          setRowsPerPage(rpp);
          setPage(1);
        }}
        sx={{ pt: 1, flexShrink: 0 }}
      />
    </Box>
  );
};

export const GateMovementsHistoryModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('gateMovementsHistoryModal');

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={{ maxWidth: 'xl' }}
      authorization={
        state.open
          ? {
              access: Access.ViewGateMovementsHistory,
              deviceId: state.gateId,
            }
          : undefined
      }
    >
      <ModalTitle
        onClose={onClose}
        title={<Trans>Ver historial de movimientos</Trans>}
        subtitle={
          state.open ? (
            <DeviceNameSpan
              deviceId={state.gateId}
              variant="subtitle1"
              color="text.secondary"
              lineHeight={1}
            />
          ) : null
        }
      />
      <ModalContent sx={{ height: 600, display: 'flex', flexDirection: 'column' }}>
        {state.open && state.gateId && (
          <ApolloErrorBoundary variant="section-alert">
            <Suspense fallback={<Skeleton height="100%" variant="rounded" />}>
              <PaginatedHistoryContent gateId={state.gateId} />
            </Suspense>
          </ApolloErrorBoundary>
        )}
      </ModalContent>
      <ModalActions onClose={onClose} />
    </GlobalModal>
  );
};
